import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Receipt } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/common/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReceiptScanButton, ReceiptView } from "@/components/finance/ReceiptScan";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

export const EXPENSE_CATS = {
  alimentation: "Alimentation", logement: "Logement", energie: "Énergie", transport: "Transport", sante: "Santé",
  loisirs: "Loisirs", abonnements: "Abonnements", shopping: "Shopping", autre: "Autre", banque: "Opérations bancaires",
};
const today = () => new Date().toISOString().slice(0, 10);

const ExpenseForm = ({ base, tid, onDone }) => {
  const [v, setV] = useState({ label: "", amount: "", category: "alimentation", date: today(), receipt_id: null });
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const scanned = ({ receipt_id, suggestion: s }) => setV((cur) => ({
    ...cur, receipt_id, label: s?.label || cur.label, amount: s?.amount != null ? String(s.amount) : cur.amount,
    category: s?.category || cur.category, date: s?.date && s.date <= today() ? s.date : cur.date,
  }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`${base}/expenses`, { ...v, amount: Number(v.amount) });
      setV({ ...v, label: "", amount: "", receipt_id: null });
      toast.success("Dépense ajoutée");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[1fr_7rem_10rem_12rem_auto]" data-testid={`${tid}-form`}>
      <ReceiptScanButton tid={tid} receiptId={v.receipt_id} onScanned={scanned} onClear={() => setV({ ...v, receipt_id: null })} />
      <Input value={v.label} onChange={set("label")} placeholder="Ex. Courses Carrefour" required minLength={2} data-testid={`${tid}-label-input`} />
      <Input type="number" min="0.01" step="0.01" value={v.amount} onChange={set("amount")} placeholder="Montant" required data-testid={`${tid}-amount-input`} />
      <Select value={v.category} onValueChange={set("category")}>
        <SelectTrigger data-testid={`${tid}-category-select`}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(EXPENSE_CATS).filter(([k]) => k !== "banque").map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <DatePicker value={v.date} onChange={set("date")} required noFuture placeholder="Date de la dépense" data-testid={`${tid}-date-input`} />
      <Button type="submit" className="rounded-full" data-testid={`${tid}-submit-button`}>Ajouter</Button>
    </form>
  );
};

export const ExpensesCard = ({ base = "/finance", tid = "expense", title = "Dépenses saisies", text = "Vos dépenses hors banque (espèces, chèques…). Personnel et privé." }) => {
  const qc = useQueryClient();
  const { money, date } = useFormat();
  const list = useQuery({ queryKey: ["expenses", base], queryFn: () => api.get(`${base}/expenses`).then((r) => r.data) });
  const refresh = () => ["expenses", "overview", "budgets", "suppliers", "dashboard", "expense-summary"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const del = async (id) => { try { await api.delete(`${base}/expenses/${id}`); refresh(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <section className="card-surface tone-wash tone-peach space-y-5 p-6" data-testid={`${tid}s-card`}>
      <div className="flex items-center gap-3">
        <span className="tone-chip flex h-9 w-9 items-center justify-center rounded-xl"><Receipt className="h-4 w-4" /></span>
        <div className="flex-1"><h2 className="text-lg font-bold">{title}</h2><p className="text-xs text-muted-foreground">{text}</p></div>
      </div>
      <ExpenseForm base={base} tid={tid} onDone={refresh} />
      {list.data?.length ? (
        <ul className="divide-y divide-border" data-testid={`${tid}s-list`}>
          {list.data.slice(0, 10).map((e) => (
            <li key={e.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`${tid}-row-${e.id}`}>
              <span className="min-w-0 flex-1 truncate">{e.label} <span className="text-xs text-muted-foreground">· {EXPENSE_CATS[e.category]} · {date(e.date)}{e.author ? ` · ${e.author}` : ""}</span></span>
              <span className="num">{money(e.amount, { maximumFractionDigits: 2 })}</span>
              {e.receipt_id && <ReceiptView id={e.receipt_id} tid={tid} />}
              {e.can_delete !== false && <ConfirmDelete title="Supprimer cette dépense ?" text={`« ${e.label} » sera retirée du suivi.`} testid={`${tid}-delete-${e.id}`} onConfirm={() => del(e.id)} />}
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-muted-foreground">Aucune dépense saisie.</p>}
    </section>
  );
};
